import { useEffect, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { rpcCall } from "../ws/client";
import { DealRequest, Order } from "../ws/types";
import { toast } from "./ui/Toast";
import { useQAProfile, isLocalhost } from "../hooks/useQAProfile";
import { TimeoutIndicator } from "./TimeoutIndicator";
import "../styles/DealRequests.scss";
import "../styles/TimeoutIndicator.scss";

const DEAL_REQUEST_TIMEOUT_MINUTES = 10;

export default function DealRequests() {
    const wallet = useTonWallet();
    const { isQAMode, profile, buyerWallet, toggleQA, switchProfile, currentWallet: qaWallet } = useQAProfile(wallet?.account?.address);
    
    const navigate = useNavigate();
    const effectiveWallet = qaWallet || wallet?.account?.address || "";
    const [requests, setRequests] = useState<DealRequest[]>([]);
    const [orders, setOrders] = useState<Map<string, Order>>(new Map());
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("pending");
    const [filteredRequests, setFilteredRequests] = useState<DealRequest[]>([]);
    const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
    const [paymentDetails, setPaymentDetails] = useState<any[]>([]);
    const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState<{ requestId: string; orderId: string } | null>(null);
    const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<string[]>([]);

    useEffect(() => {
        if (effectiveWallet) {
            loadRequests();
            loadPaymentDetails();
        }
    }, [effectiveWallet, profile]);

    const loadPaymentDetails = async () => {
        if (!effectiveWallet) return;
        
        try {
            const response = await rpcCall<{}, { payment_details: any[] }>(
                "payment_details.list",
                { wallet: effectiveWallet }
            );
            
            if (response.result?.payment_details) {
                setPaymentDetails(response.result.payment_details);
            }
        } catch (err) {
            console.error("Failed to load payment details:", err);
        }
    };

    const loadRequests = async () => {
        if (!effectiveWallet) return;
        
        // In QA mode, only show incoming requests when profile is 'seller'
        if (isQAMode && profile !== 'seller') {
            setRequests([]);
            setOrders(new Map());
            return;
        }

        setLoading(true);
        try {
            const response = await rpcCall<
                { order_creator: string },
                { requests: DealRequest[] }
            >("deal_requests.list", {
                order_creator: effectiveWallet
            });

            if (response.result?.requests) {
                setRequests(response.result.requests);
                
                // Load order info for each request
                const orderMap = new Map<string, Order>();
                for (const req of response.result.requests) {
                    const orderResp = await rpcCall<
                        { order_id: string },
                        { order: Order }
                    >("orders.get", { order_id: req.order_id });
                    
                    if (orderResp.result?.order) {
                        orderMap.set(req.order_id, orderResp.result.order);
                    }
                }
                setOrders(orderMap);
            }
        } catch (err) {
            console.error("Failed to load requests:", err);
            toast.error("Не удалось загрузить заявки");
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async (requestId: string) => {
        if (!effectiveWallet) return;

        // Find the request and order
        const request = requests.find(r => r.id === requestId);
        if (!request) return;
        
        const order = orders.get(request.order_id);
        if (!order) return;

        // В DealRequests мы ПРИНИМАЕМ заявки на НАШИ ордера
        // effectiveWallet = создатель ордера (order.creator)
        // request.requester = тот кто откликнулся
        //
        // Логика: кто продавец криптовалюты?
        // - SELL ордер: creator (мы) = продавец → нужно спросить НАШИ реквизиты
        // - BUY ордер: requester (откликнувшийся) = продавец → его реквизиты автоматически на беке
        
        // Если это SELL ордер, мы - продавец, нужно выбрать наши реквизиты
        if (order.side === "sell") {
            setShowPaymentDetailsModal({ requestId, orderId: order.id });
            return;
        }

        // Для BUY ордера реквизиты откликнувшегося (продавца) будут взяты автоматически на беке
        if (!confirm("Вы уверены, что хотите принять эту заявку и начать сделку?")) {
            return;
        }

        await acceptRequest(requestId, []);
    };

    const acceptRequest = async (requestId: string, paymentDetailsIds: string[]) => {
        if (!effectiveWallet) return;

        setProcessingRequestId(requestId);
        try {
            const response = await rpcCall(
                "deal_requests.accept",
                {
                    request_id: requestId,
                    order_creator: effectiveWallet,
                    payment_details_ids: paymentDetailsIds
                }
            );

            if (response.error) {
                toast.error(response.error.message);
            } else {
                toast.success("Заявка принята! Сделка создана.");
                setShowPaymentDetailsModal(null);
                setSelectedPaymentDetails([]);
                loadRequests();
                navigate(`/?tab=my-deals`);
            }
        } catch (err) {
            console.error(err);
            toast.error("Ошибка при принятии заявки");
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleReject = async (requestId: string) => {
        if (!effectiveWallet) return;

        const reason = prompt("Укажите причину отклонения (необязательно):");

        setProcessingRequestId(requestId);
        try {
            const response = await rpcCall(
                "deal_requests.reject",
                {
                    request_id: requestId,
                    order_creator: effectiveWallet,
                    reason: reason || undefined
                }
            );

            if (response.error) {
                toast.error(response.error.message);
            } else {
                toast.success("Заявка отклонена");
                loadRequests();
            }
        } catch (err) {
            console.error(err);
            toast.error("Ошибка при отклонении заявки");
        } finally {
            setProcessingRequestId(null);
        }
    };

    useEffect(() => {
        if (statusFilter === "all") {
            setFilteredRequests(requests);
        } else {
            setFilteredRequests(requests.filter(r => r.status === statusFilter));
        }
    }, [requests, statusFilter]);

    if (!wallet && !isQAMode) {
        return (
            <div className="deal-requests">
                <h2>Заявки на мои ордера</h2>
                <div className="empty-state">
                    <p>Подключите кошелёк для просмотра заявок</p>
                </div>
            </div>
        );
    }

    return (
        <div className="deal-requests">
            {/* QA Mode Controls */}
            {isQAMode && (
                <div className="qa-controls">
                    <div className="qa-toggle">
                        <label className="qa-switch">
                            <input 
                                type="checkbox" 
                                checked={isQAMode}
                                onChange={toggleQA}
                            />
                            <span className="qa-slider"></span>
                        </label>
                        <span className="qa-label">🔧 QA Mode</span>
                    </div>
                    <div className="qa-profile">
                        <span className={`profile-badge ${profile}`}>
                            {profile === 'seller' ? '🏪 Продавец' : '🛒 Покупатель'}
                        </span>
                        <button className="btn btn-small" onClick={switchProfile}>
                            🔄 Сменить профиль
                        </button>
                    </div>
                </div>
            )}

            <div className="header">
                <h2>Заявки на мои ордера</h2>
                <div className="header-actions">
                    {!isQAMode && isLocalhost() && (
                        <label className="qa-toggle-inline">
                            <input 
                                type="checkbox" 
                                checked={isQAMode}
                                onChange={toggleQA}
                            />
                            🔧 QA Mode
                        </label>
                    )}
                    <select 
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Все статусы</option>
                        <option value="pending">Ожидающие</option>
                        <option value="accepted">Принятые</option>
                        <option value="rejected">Отклонённые</option>
                        <option value="cancelled">Отменённые</option>
                    </select>
                    <button className="btn btn-secondary" onClick={loadRequests} disabled={loading}>
                        {loading ? "Загрузка..." : "Обновить"}
                    </button>
                </div>
            </div>

            {loading && requests.length === 0 && <p>Загрузка...</p>}

            {!loading && filteredRequests.length === 0 && (
                <div className="empty-state">
                    <p>Нет заявок с выбранным фильтром</p>
                    {isQAMode && profile === 'seller' && (
                        <p className="hint">💡 Переключитесь на "Покупатель" чтобы создать заявку</p>
                    )}
                </div>
            )}

            <div className="requests-list">
                {filteredRequests.map((request) => {
                    const order = orders.get(request.order_id);
                    if (!order) return null;

                    return (
                        <div key={request.id} className="request-card">
                            <div className="request-header">
                                <span className={`badge badge-${request.status}`}>
                                    {request.status === "pending" ? "Новая заявка" : 
                                     request.status === "accepted" ? "Принята" :
                                     request.status === "rejected" ? "Отклонена" :
                                     request.status === "cancelled" ? "Отменена" : request.status}
                                </span>
                                <span className="time">
                                    {new Date(request.created_at).toLocaleString("ru-RU")}
                                </span>
                            </div>

                            {/* Timeout indicator for pending requests */}
                            {request.status === "pending" && (
                                <TimeoutIndicator
                                    lastUpdateTime={request.created_at}
                                    timeoutMinutes={DEAL_REQUEST_TIMEOUT_MINUTES}
                                    currentStep="pending"
                                />
                            )}

                            <div className="order-info">
                                <h3>
                                    {order.side === "sell" ? "Продажа" : "Покупка"}{" "}
                                    {request.amount || order.amount} {order.jetton_symbol}
                                </h3>
                                <div className="details">
                                    <div className="detail-row">
                                        <span className="label">Цена:</span>
                                        <span className="value">
                                            {order.price} {order.fiat_currency}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Итого:</span>
                                        <span className="value">
                                            {((request.amount || order.amount) * order.price).toFixed(2)} {order.fiat_currency}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="requester-info">
                                <span className="label">От:</span>
                                <code>{request.requester.slice(0, 8)}...</code>
                            </div>

                            {request.message && (
                                <div className="message">
                                    Дополнение: <p>{request.message}</p>
                                    <br/>
                                </div>
                            )}

                            <div className="request-actions">
                                {request.status === "pending" && (
                                    <>
                                        <button
                                            className="btn btn-success"
                                            onClick={() => handleAccept(request.id)}
                                            disabled={processingRequestId === request.id}
                                        >
                                            {processingRequestId === request.id ? (
                                                <>
                                                    <span className="spinner" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                                                    Обработка...
                                                </>
                                            ) : (
                                                'Принять'
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-danger btn-outline"
                                            onClick={() => handleReject(request.id)}
                                            disabled={processingRequestId === request.id}
                                        >
                                            {processingRequestId === request.id ? (
                                                <>
                                                    <span className="spinner" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                                                    Обработка...
                                                </>
                                            ) : (
                                                'Отклонить'
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Payment Details Selection Modal */}
            {showPaymentDetailsModal && (
                <div className="modal-overlay" onClick={() => setShowPaymentDetailsModal(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Выберите реквизиты для оплаты</h3>
                            <button className="close-btn" onClick={() => setShowPaymentDetailsModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <p>Вы принимаете заявку на покупку крипты. Выберите ваши реквизиты, куда покупатель отправит оплату:</p>
                            
                            {paymentDetails.length === 0 ? (
                                <div className="no-details-warning">
                                    <p>❌ У вас нет сохранённых реквизитов!</p>
                                    <p>Перейдите в раздел "💳 Реквизиты" чтобы добавить их.</p>
                                </div>
                            ) : (
                                <div className="payment-details-list">
                                    {paymentDetails.map((detail: any) => (
                                        <div
                                            key={detail.id}
                                            className={`payment-detail-option ${selectedPaymentDetails.includes(detail.id) ? 'selected' : ''}`}
                                            onClick={() => {
                                                if (selectedPaymentDetails.includes(detail.id)) {
                                                    setSelectedPaymentDetails(selectedPaymentDetails.filter(id => id !== detail.id));
                                                } else {
                                                    setSelectedPaymentDetails([...selectedPaymentDetails, detail.id]);
                                                }
                                            }}
                                        >
                                            <div className="detail-method">{detail.payment_method}</div>
                                            <div className="detail-account">
                                                {detail.account_number}
                                                {detail.bank_name && ` • ${detail.bank_name}`}
                                            </div>
                                            {selectedPaymentDetails.includes(detail.id) && (
                                                <div className="checkmark">✓</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={() => {
                                    setShowPaymentDetailsModal(null);
                                    setSelectedPaymentDetails([]);
                                }}
                            >
                                Отмена
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={() => {
                                    if (selectedPaymentDetails.length === 0) {
                                        toast.error("Выберите хотя бы один реквизит");
                                        return;
                                    }
                                    acceptRequest(showPaymentDetailsModal.requestId, selectedPaymentDetails);
                                }}
                                disabled={processingRequestId !== null || selectedPaymentDetails.length === 0}
                            >
                                {processingRequestId ? 'Обработка...' : 'Принять заявку'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
